<script lang="ts">
  import { onMount } from 'svelte'
  import { api } from '$lib/api/client'
  import { showApiError } from '$lib/util/errors'

  interface Stat {
    label: string
    value: string
  }

  let stats = $state<Stat[]>([])
  let loading = $state(true)

  function summarize(items: unknown[] | undefined, nextCursor: number | undefined): string {
    if (!items) return '-'
    if (nextCursor !== undefined) return `${items.length}+`
    return String(items.length)
  }

  async function load(): Promise<void> {
    loading = true
    try {
      const [people, animals, occ] = await Promise.all([
        api.GET('/people', { params: { query: { limit: 50 } } }),
        api.GET('/animals', { params: { query: { limit: 50 } } }),
        api.GET('/occupancies', { params: { query: { limit: 50, status: 'ACTIVE' } } }),
      ])

      const peopleError = (people as unknown as { error?: unknown }).error
      const animalsError = (animals as unknown as { error?: unknown }).error
      const occError = (occ as unknown as { error?: unknown }).error
      if (peopleError) showApiError(peopleError)
      if (animalsError) showApiError(animalsError)
      if (occError) showApiError(occError)

      stats = [
        { label: 'People', value: summarize(people.data?.items, people.data?.nextCursor) },
        { label: 'Animals', value: summarize(animals.data?.items, animals.data?.nextCursor) },
        { label: 'Active occupancies', value: summarize(occ.data?.items, occ.data?.nextCursor) },
      ]
    } finally {
      loading = false
    }
  }

  onMount(() => {
    void load()
  })
</script>

<h1>Dashboard</h1>

<div class="stats-grid">
  {#if loading}
    <div class="stat-card loading">Loading…</div>
  {:else}
    {#each stats as stat (stat.label)}
      <div class="stat-card">
        <div class="stat-value">{stat.value}</div>
        <div class="stat-label">{stat.label}</div>
      </div>
    {/each}
  {/if}
</div>

<style>
  h1 {
    margin: 0 0 24px;
    font-size: 1.75rem;
  }
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
  }
  .stat-card {
    background: var(--surface-2);
    padding: 24px;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    text-align: center;
  }
  .stat-card.loading {
    color: var(--muted);
  }
  .stat-value {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--brand);
  }
  .stat-label {
    font-size: 0.875rem;
    color: var(--muted);
    margin-top: 4px;
  }
</style>
